/**
 * @fileoverview Define shared archive source identity and review-status presentation used by document workspaces. These helpers connect backend metadata to preview/download and review controls. Canonical bytes, access, and classification writes remain backend-owned; presentation status must not manufacture a completed review or a current revision.
 */

import api from '../../api';



export function apiUrl(path) {
  const base = (api.defaults && api.defaults.baseURL) || '';
  const value = String(path || '');
  if (/^https?:\/\//i.test(value)) return value;
  if (base.endsWith('/api') && value.startsWith('/api/')) {
    return `${base.slice(0, -4)}${value}`;
  }
  return `${base}${value}`;
}



export function formatSize(bytes, fallback = '-') {
  if (bytes == null) return fallback;
  const value = Number(bytes);
  if (!Number.isFinite(value)) return fallback;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}



export function parseDateValue(value) {
  if (value == null || value === '') return null;
  let date;
  let includesTime = true;
  const rawValue = String(value).trim();
  const dateOnlyMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    includesTime = false;
    date = new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3])
    );
  } else {
    const microsoftDateMatch = rawValue.match(/^\/Date\((-?\d+)\)\/$/);
    const timestampValue = microsoftDateMatch ? microsoftDateMatch[1] : rawValue;
    const asNumber = Number(timestampValue);
    date = Number.isFinite(asNumber) ?
      new Date(Math.abs(asNumber) < 100000000000 ? asNumber * 1000 : asNumber) :
      new Date(rawValue);
  }

  if (Number.isNaN(date.getTime())) return null;
  return { date, includesTime };
}



export function formatCreated(value) {
  const parsed = parseDateValue(value);
  if (!parsed) {
    const fallback = value == null || value === '' ? '-' : String(value);
    return { date: fallback, time: '', title: fallback, dateTime: undefined };
  }

  const date = parsed.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = parsed.includesTime ?
    parsed.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) :
    '';
  const title = parsed.includesTime ?
    parsed.date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) :
    date;
  return { date, time, title, dateTime: parsed.date.toISOString() };
}



export function isDocxVersion(version) {
  if (version?.is_docx === true) return true;
  if (version?.is_docx === false) return false;
  const filename = String(version?.filename || '').toLowerCase();
  const mimeType = String(version?.mime_type || '').toLowerCase();
  return filename.endsWith('.docx') || mimeType.includes('wordprocessingml.document');
}



export function currentVersion(document, versions = []) {
  const exact = versions.find((version) => version.revision_id === document?.latest_revision_id);
  if (exact) return exact;
  const marked = versions.filter((version) => version.is_latest);
  return [...(marked.length ? marked : versions)].sort((left, right) => (
    Number(right.version_index || 0) - Number(left.version_index || 0) ||
    String(right.revision_id || '').localeCompare(String(left.revision_id || ''))
  ))[0] || null;
}



export function versionLabel(version) {
  if (!version) return '-';
  const number = String(
    version.version_number ||
    version.semantic_version ||
    Number(version.version_index || 0) + 1
  ).trim();
  return `Version ${number}`;
}
